import React, { useState, useEffect } from "react";

const TestimonialCarousel = ({ testimonials, autoPlayDuration = 8000 }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const totalSlides = testimonials.length;

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % totalSlides);
    }, autoPlayDuration);
    return () => clearInterval(interval);
  }, [totalSlides, autoPlayDuration]);

  return (
    <div className="bg-white rounded-xl shadow-2xl overflow-hidden h-full flex flex-col relative min-h-[500px] sm:min-h-[600px]">
      <div className="relative flex-1 h-full">
        {testimonials.map((item, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-700 p-6 sm:p-8 lg:p-10 flex flex-col justify-between ${
              activeSlide === index
                ? "opacity-100 z-10 translate-x-0"
                : "opacity-0 z-0 translate-x-8"
            }`}
          >
            {item.type === "testimonial" && (
              <div className="h-full flex flex-col pb-12 sm:pb-16">
                <div className="mb-4 sm:mb-6 min-h-[52px] flex items-center p-2 rounded">
                  <img
                    src={item.logo}
                    alt="Company Logo"
                    className="h-12 w-auto object-contain max-w-[164px]"
                    style={{ display: "block" }}
                    onLoad={(e) => {
                      console.log("Logo loaded successfully:", item.logo);
                    }}
                    onError={(e) => {
                      console.error("Logo failed to load:", item.logo);
                      e.target.style.display = "none";
                    }}
                  />
                </div>

                <blockquote className="flex-1 text-gray-600 text-sm sm:text-base lg:text-lg leading-relaxed italic mb-6 sm:mb-8">
                  "{item.text}"
                </blockquote>

                <div className="flex items-center mt-auto pt-4 sm:pt-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden mr-3 sm:mr-4 border-2 border-gray-100 flex-shrink-0">
                    <img
                      src={item.avatar}
                      alt={item.author}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src =
                          "https://ui-avatars.com/api/?name=" + item.author;
                      }}
                    />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-base sm:text-lg">
                      {item.author}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500 font-medium">
                      {item.position}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {item.type === "case-study" && (
              <div className="h-full flex flex-col pb-12 sm:pb-16">
                <div className="mb-6 sm:mb-8 rounded-xl overflow-hidden h-48 sm:h-56 bg-gray-100 shadow-inner">
                  <img
                    src={item.image}
                    alt="Case study"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
                <div className="space-y-4 sm:space-y-6 flex-1">
                  {item.stats.map((stat, idx) => (
                    <div key={idx}>
                      <div className="text-xl sm:text-2xl font-bold text-gray-900">
                        {stat.title}
                      </div>
                      <div className="text-gray-600 text-base sm:text-lg mt-1">
                        {stat.subtitle}
                      </div>
                    </div>
                  ))}
                </div>
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-primary-700 hover:text-primary-800 text-base sm:text-lg font-bold mt-auto group"
                >
                  {item.linkText}
                  <svg
                    className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </a>
              </div>
            )}

            {item.type === "benefits" && (
              <div className="h-full flex flex-col justify-center space-y-4 sm:space-y-6 pb-12 sm:pb-16">
                {item.items.map((benefit, idx) => (
                  <div key={idx} className="flex items-start">
                    <div className="text-2xl sm:text-3xl lg:text-4xl mr-3 sm:mr-4">
                      {benefit.icon}
                    </div>
                    <div className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 flex-1">
                      {benefit.text}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Dots */}
      <div className="p-4 sm:p-6 flex justify-center space-x-2 sm:space-x-3 bg-white z-20 absolute bottom-0 w-full">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => setActiveSlide(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              activeSlide === index
                ? "bg-gray-800 w-8"
                : "bg-gray-300 w-2 hover:bg-gray-400"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialCarousel;
